/**
 * <bundle-selector>
 *
 * Lets a customer switch between sets of a product on the product page.
 *
 * Product options are real products. Picking one refetches the product section through the
 * Section Rendering API and swaps it in place, so the gallery, price, badges, inventory and
 * the add-to-cart target all follow the selected set without a full page load. The options
 * are rendered as links, so the whole thing degrades to normal navigation without JS.
 *
 * Quantity options stay on the current product and only write to the quantity input, with an
 * optional jump to a matching gallery image.
 */
if (!customElements.get('bundle-selector')) {
  customElements.define(
    'bundle-selector',
    class BundleSelector extends HTMLElement {
      // Section HTML is shared across instances so hovering an option warms the cache for it.
      static cache = new Map();

      // Resolved once from the server-rendered title, then reused for every swap.
      static titleSuffix = undefined;

      constructor() {
        super();

        this.abortController = undefined;
        this.onPopState = this.handlePopState.bind(this);
      }

      get sectionId() {
        return this.getAttribute('data-section-id');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get updateUrl() {
        return this.getAttribute('data-update-url') !== 'false';
      }

      get section() {
        return document.getElementById(`shopify-section-${this.sectionId}`);
      }

      get productForm() {
        return document.forms[this.getAttribute('form')];
      }

      get quantityInput() {
        return this.section?.querySelector('quantity-input input');
      }

      get mediaGallery() {
        return this.section?.querySelector('media-gallery');
      }

      get options() {
        return this.querySelectorAll('[data-bundle-option]');
      }

      connectedCallback() {
        this.options.forEach((option) => {
          option.addEventListener('click', this.onOptionClick.bind(this, option));

          const url = option.getAttribute('data-product-url');
          if (url) {
            option.addEventListener('pointerenter', () => this.prefetch(url), { once: true });
          }
        });

        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        window.removeEventListener('popstate', this.onPopState);
        this.abortController?.abort();
      }

      onOptionClick(option, event) {
        if (option.classList.contains('is-selected')) {
          event.preventDefault();
          return;
        }

        const quantity = option.getAttribute('data-quantity');
        if (quantity) {
          event.preventDefault();
          this.selectQuantity(option, quantity);
          return;
        }

        const url = option.getAttribute('data-product-url');
        if (!url) return;

        // Let modified clicks (new tab, download, ...) behave like a normal link.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

        event.preventDefault();
        this.selectProduct(url, true);
      }

      selectQuantity(option, quantity) {
        this.options.forEach((item) => {
          item.classList.toggle('is-selected', item === option);
          item === option ? item.setAttribute('aria-current', 'true') : item.removeAttribute('aria-current');
        });

        const input = this.quantityInput;
        if (input) {
          input.value = quantity;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const position = option.getAttribute('data-media-position');
        if (position) this.showMediaAtPosition(position);
      }

      showMediaAtPosition(position) {
        const gallery = this.mediaGallery;
        if (!gallery) return;

        const media = gallery.querySelectorAll('[data-media-id]');
        const target = media[parseInt(position, 10) - 1];
        if (!target) return;

        if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(target.getAttribute('data-media-id'));
        } else {
          target.scrollIntoView({ block: 'start', behavior: theme.config.motionReduced ? 'auto' : 'smooth' });
        }
      }

      async selectProduct(url, pushUrl) {
        const section = this.section;
        if (!section) return;

        this.abortController?.abort();
        this.abortController = new AbortController();

        section.setAttribute('aria-busy', 'true');
        this.classList.add('is-loading');

        try {
          const html = await this.fetchSection(url, this.abortController.signal);
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const source = parsed.getElementById(`shopify-section-${this.sectionId}`) ?? parsed.body;

          if (pushUrl && this.updateUrl) {
            window.history.pushState({ bundleSelector: this.sectionId, url }, '', url);
          }

          // Rebuilding the section re-runs every custom element inside it, which is what keeps
          // the gallery, price and product form consistent with the newly selected product.
          theme.HTMLUpdateUtility.setInnerHTML(section, source.innerHTML);
          this.updateDocumentTitle(section);

          section.dispatchEvent(
            new CustomEvent('bundle:change', {
              bubbles: true,
              detail: { sectionId: this.sectionId, url }
            })
          );
        } catch (error) {
          if (error.name === 'AbortError') return;

          console.error(error);
          // A failed swap must not strand the customer on the wrong set.
          window.location.href = url;
        } finally {
          section.removeAttribute('aria-busy');
          this.classList.remove('is-loading');
        }
      }

      /**
       * The Section Rendering API only returns the section, so there is no <title> to copy.
       * Rebuild it from the new heading and the shop suffix the page was served with, so the
       * browser tab follows the selected set instead of keeping the first product's title.
       */
      updateDocumentTitle(section) {
        const heading = section.querySelector('h1')?.textContent.trim();
        if (!heading) return;

        if (BundleSelector.titleSuffix === undefined) {
          const match = document.title.match(/^.*\s[–—|]\s(.+)$/);
          BundleSelector.titleSuffix = match ? match[1] : null;
        }

        document.title = BundleSelector.titleSuffix ? `${heading} – ${BundleSelector.titleSuffix}` : heading;
      }

      async fetchSection(url, signal) {
        const requestUrl = this.buildRequestUrl(url);

        if (BundleSelector.cache.has(requestUrl)) {
          return BundleSelector.cache.get(requestUrl);
        }

        const response = await fetch(requestUrl, { signal });
        if (!response.ok) throw new Error(`Bundle section request failed: ${response.status}`);

        const html = await response.text();
        BundleSelector.cache.set(requestUrl, html);

        return html;
      }

      buildRequestUrl(url) {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.set('section_id', this.sectionId);
        return requestUrl.toString();
      }

      prefetch(url) {
        this.fetchSection(url).catch(() => {});
      }

      handlePopState(event) {
        if (!event.state?.bundleSelector && !this.isBundleUrl(window.location.pathname)) return;
        if (event.state?.bundleSelector && event.state.bundleSelector !== this.sectionId) return;

        this.selectProduct(window.location.pathname + window.location.search, false);
      }

      isBundleUrl(pathname) {
        return Array.from(this.options).some((option) => {
          const url = option.getAttribute('data-product-url');
          return url && new URL(url, window.location.origin).pathname === pathname;
        });
      }
    }
  );
}
